import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { History } from "lucide-react";
import { api, fmtFecha } from "@/lib/api";
import { Badge, Card, PageHeader, inputCls } from "@/components/rrhh/ui";

const ACCIONES = {
  pin_blanqueo: { label: "Blanqueó un PIN", color: "rojo" },
  pin_desbloqueo: { label: "Desbloqueó un PIN", color: "rojo" },
  pin_reseteo: { label: "Reseteó un PIN", color: "rojo" },
  fichada_correccion: { label: "Corrigió una fichada", color: "amarillo" },
  fichada_anulada: { label: "Anuló una fichada", color: "amarillo" },
  jornada_cierre_manual: { label: "Cerró una jornada a mano", color: "violeta" },
  novedad_marcar: { label: "Marcó una novedad", color: "violeta" },
  novedad_quitar: { label: "Quitó una novedad", color: "violeta" },
  justificativo_aprobado: { label: "Aprobó un justificativo", color: "verde" },
  justificativo_rechazado: { label: "Rechazó un justificativo", color: "verde" },
  justificativo_cargado: { label: "Cargó un justificativo", color: "verde" },
  calendario_feriado: { label: "Cargó un feriado", color: "azul" },
  calendario_vacaciones: { label: "Cargó vacaciones", color: "azul" },
  calendario_licencia: { label: "Cargó una licencia", color: "azul" },
  feriado_eliminado: { label: "Eliminó un feriado", color: "azul" },
  empleado_alta: { label: "Alta de empleado", color: "gris" },
  empleado_edicion: { label: "Editó un empleado", color: "gris" },
  empleado_baja: { label: "Baja de empleado", color: "gris" },
  demo_reloj_fijar: { label: "Fijó el reloj de demo", color: "gris" },
  demo_reloj_real: { label: "Reloj a hora real", color: "gris" },
  comprobante_visto: { label: "Vió un comprobante o foto", color: "gris" },
};

export default function HistorialPage() {
  const [empleadoId, setEmpleadoId] = useState("");
  const [accion, setAccion] = useState("");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");

  const { data: empleados } = useQuery({
    queryKey: ["empleados"],
    queryFn: async () => (await api.get("/empleados")).data,
  });
  const { data: filas, isLoading } = useQuery({
    queryKey: ["historial", empleadoId, accion, desde, hasta],
    queryFn: async () => (await api.get("/rrhh/historial", {
      params: {
        empleado_id: empleadoId || undefined, accion: accion || undefined,
        fecha_desde: desde || undefined, fecha_hasta: hasta || undefined,
      },
    })).data,
  });

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }} data-testid="pagina-historial">
      <PageHeader
        titulo="Historial de cambios"
        subtitulo="Todo lo que RRHH y Liquidación cambiaron a mano. No se borra."
        testid="header-historial"
      />

      <Card testid="card-filtros-historial" className="mb-5" bodyCls="p-4">
        <div className="flex flex-wrap items-center gap-2">
          <select value={empleadoId} onChange={(e) => setEmpleadoId(e.target.value)} className={inputCls} data-testid="filtro-empleado-historial">
            <option value="">Todas las personas</option>
            {empleados?.map((e) => <option key={e.id} value={e.id}>{e.apellido}, {e.nombre} ({e.legajo})</option>)}
          </select>
          <select value={accion} onChange={(e) => setAccion(e.target.value)} className={inputCls} data-testid="filtro-accion-historial">
            <option value="">Todas las acciones</option>
            {Object.entries(ACCIONES).map(([id, a]) => <option key={id} value={id}>{a.label}</option>)}
          </select>
          <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className={inputCls} data-testid="filtro-desde-historial" />
          <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className={inputCls} data-testid="filtro-hasta-historial" />
        </div>
      </Card>

      <Card testid="card-historial" bodyCls="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/8 text-left text-[11px] uppercase tracking-wider text-gray-500">
                <th className="px-5 py-3 font-semibold">Fecha y hora</th>
                <th className="px-4 py-3 font-semibold">Usuario</th>
                <th className="px-4 py-3 font-semibold">Acción</th>
                <th className="px-4 py-3 font-semibold">Empleado</th>
                <th className="px-4 py-3 font-semibold">Detalle</th>
                <th className="px-5 py-3 font-semibold">Motivo</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={6} className="px-5 py-10 text-center text-gray-500">Cargando…</td></tr>}
              {filas?.map((r) => {
                const a = ACCIONES[r.accion] || { label: r.accion, color: "gris" };
                return (
                  <tr key={r.id} className="border-b border-white/5 last:border-0" data-testid={`historial-${r.id.slice(0, 8)}`}>
                    <td className="whitespace-nowrap px-5 py-3 font-mono-num text-xs text-gray-400">
                      {fmtFecha(r.fecha_hora)} {r.fecha_hora.slice(11, 16)}
                    </td>
                    <td className="px-4 py-3">
                      <span className="rounded-full bg-white/10 px-2.5 py-1 text-[11px] font-semibold text-gray-300">{r.usuario}</span>
                    </td>
                    <td className="px-4 py-3"><Badge color={a.color}>{a.label}</Badge></td>
                    <td className="px-4 py-3 text-white">
                      {r.empleado_nombre
                        ? <>{r.empleado_nombre} <span className="font-mono-num text-xs text-gray-500">({r.legajo})</span></>
                        : <span className="text-gray-600">—</span>}
                    </td>
                    <td className="max-w-72 px-4 py-3 text-[13px] text-gray-300">{r.detalle}</td>
                    <td className="max-w-56 px-5 py-3 text-xs italic text-gray-500">{r.motivo ? `“${r.motivo}”` : "—"}</td>
                  </tr>
                );
              })}
              {filas?.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-5 py-12 text-center">
                    <History size={26} className="mx-auto text-gray-600" />
                    <p className="mt-3 text-sm font-semibold text-gray-300">Todavía no hay cambios</p>
                    <p className="text-xs text-gray-500">Cuando alguien corrija algo a mano, aparece acá.</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </motion.div>
  );
}
